import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { SourcesLink } from "../components/SourcesLink";
import { DataStamp } from "../components/DataLabel";
import { ArrowRight, Download, ExternalLink } from "lucide-react";
import { api, API } from "../lib/api";
import { Reveal, useSeo } from "../lib/ui";
import { StageBar, StageClose } from "../components/StageChain";
import { AfconBand } from "../components/TrackAnchor";

const usd = (n) =>
  n >= 1e6 ? `$${(n / 1e6).toFixed(2)}m` : n >= 1e3 ? `$${Math.round(n).toLocaleString()}` : `$${Math.round(n || 0)}`;
const ksh = (n) => (n >= 1e9 ? `KSh ${(n / 1e9).toFixed(2)}bn` : `KSh ${(n / 1e6).toFixed(0)}m`);

export default function NationalGame() {
  const [d, setD] = useState(null);
  const [err, setErr] = useState("");

  useSeo({
    title: "The National Game — KickStartKenyaFootball",
    description:
      "Stage four: eighteen Harambee Stars county training centres, one in each corridor county, costed. A national call-up without leaving your professional club, your county side or your schooling.",
  });

  useEffect(() => {
    api
      .get("/national-game")
      .then((r) => setD(r.data))
      .catch(() => setErr("We could not load the national game case. Refresh to try again."));
  }, []);

  if (err)
    return (
      <div className="bg-black px-5 pb-24 pt-24 md:px-10" data-testid="national-game-error">
        <p className="border border-maasai/40 bg-maasai/10 p-6 text-sm text-white">{err}</p>
      </div>
    );
  if (!d) return <div className="min-h-[60vh] bg-black" data-testid="national-game-loading" />;

  const t = d.totals;

  return (
    <div className="bg-black" data-testid="national-game-page">
      <StageBar stage="national" />

      <section className="px-5 pb-14 pt-12 md:px-10 md:pt-16">
        <div className="mx-auto max-w-[1500px]">
          <Reveal>
            <p className="eyebrow">Stage four — the national game</p>
            <h1 className="display-1 mt-5 max-w-5xl">Harambee Stars, One Camp Base Per County</h1>
            <p className="body-copy mt-8 max-w-3xl">{d.standfirst}</p>
            <p className="body-copy mt-5 max-w-3xl">{d.argument}</p>
          </Reveal>

          <div className="mt-12 grid gap-px border border-zinc-800 bg-zinc-800 sm:grid-cols-2 lg:grid-cols-4" data-testid="national-headlines">
            {[
              ["Camp bases proposed", String(t.centres), "One per corridor county"],
              ["Cost per centre", usd(t.unit_capex_usd), ksh(t.unit_capex_ksh)],
              ["All eighteen", usd(t.capex_usd), `${d.share_of_programme.pct}% of the whole programme`],
              ["Camp days a year", t.camp_days.toLocaleString(), `${t.squad_places} squad places at a time`],
            ].map(([label, value, note], i) => (
              <div key={label} className="bg-[#0b0b0b] p-6" data-testid={`national-headline-${i}`}>
                <p className="text-[0.55rem] font-bold uppercase tracking-[0.18em] text-zinc-400">{label}</p>
                <p className="mt-4 font-display text-xl font-bold leading-none text-white md:text-2xl">{value}</p>
                <p className="mt-3 text-[0.56rem] uppercase tracking-[0.14em] text-ochre">{note}</p>
              </div>
            ))}
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/plans?track=harambee" className="btn-primary" data-testid="national-steps-cta">
              The steps that build a centre <ArrowRight size={14} />
            </Link>
            <a href={`${API}/research-pack.pdf`} className="btn-ghost" data-testid="national-prospectus">
              <Download size={13} /> One-page research pack
            </a>
            <Link to="/apply?type=Harambee%20Stars%20county%20training%20centre" className="btn-ghost" data-testid="national-proposal-cta">
              Propose a training centre
            </Link>
          </div>
        </div>
      </section>

      <section className="border-t border-zinc-900 py-16">
        <div className="mx-auto max-w-[1500px] px-5 md:px-10">
          <p className="eyebrow">The pathway</p>
          <h2 className="display-2 mt-4 max-w-3xl">Nobody leaves their club to play for Kenya</h2>
          <p className="body-copy mt-5 max-w-3xl">
            A camp base does not replace the county club or the Sunday-league side. It sits on top of them. A player
            keeps his contract, keeps turning out at the weekend, and comes into camp at the nearest centre.
          </p>
          <div className="mt-10 border-t border-white/15" data-testid="national-pathway">
            {d.pathway.map((p, i) => (
              <div
                key={p.stage}
                className="grid grid-cols-[38px_minmax(0,1fr)] items-start gap-4 border-b border-white/10 py-6 md:grid-cols-[56px_minmax(0,1.1fr)_minmax(0,2fr)] md:items-center md:gap-8"
                data-testid={`national-pathway-${i}`}
              >
                <span className="mono text-sm text-maasai">{String(i + 1).padStart(2, "0")}</span>
                <span className="font-display text-lg uppercase leading-tight tracking-tight text-white md:text-2xl">
                  {p.stage}
                </span>
                <span className="text-sm leading-relaxed text-zinc-400">{p.detail}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="border-t border-zinc-900 bg-[#0b0b0b] py-16">
        <div className="mx-auto grid max-w-[1500px] gap-12 px-5 md:px-10 lg:grid-cols-2">
          <div>
            <p className="eyebrow">What a camp base has to have</p>
            <h2 className="display-3 mt-4">Built on the club base, not beside it</h2>
            <div className="mt-8 border-t border-white/15" data-testid="national-spec">
              {d.spec.map((s) => (
                <div key={s.item} className="border-b border-white/10 py-5">
                  <div className="flex items-baseline justify-between gap-4">
                    <p className="font-display text-base uppercase tracking-tight text-white md:text-lg">{s.item}</p>
                    <p className="mono shrink-0 text-sm text-ochre">{usd(s.usd)}</p>
                  </div>
                  <p className="mt-2 text-xs leading-relaxed text-zinc-400">{s.detail}</p>
                </div>
              ))}
            </div>
          </div>
          <div>
            <p className="eyebrow">Running it</p>
            <h2 className="display-3 mt-4">What a centre costs every year</h2>
            <div className="mt-8 border-t border-white/15" data-testid="national-annual">
              {d.annual_lines.map((a) => (
                <div key={a.item} className="flex items-baseline justify-between gap-4 border-b border-white/10 py-5">
                  <p className="text-sm text-white">{a.item}</p>
                  <p className="mono shrink-0 text-sm text-ochre">{usd(a.usd)}</p>
                </div>
              ))}
              <div className="flex items-baseline justify-between gap-4 bg-[#140406] px-4 py-4">
                <p className="text-[0.62rem] font-bold uppercase tracking-[0.16em] text-white">Per centre, per year</p>
                <p className="mono text-sm font-bold text-white">{usd(t.annual_per_centre_usd)}</p>
              </div>
            </div>
            <p className="mt-8 eyebrow">What it is judged on</p>
            <div className="mt-4 border-t border-white/15" data-testid="national-outcomes">
              {d.outcomes.map((o) => (
                <div key={o.outcome} className="border-b border-white/10 py-5">
                  <p className="font-display text-base uppercase tracking-tight text-white md:text-lg">{o.outcome}</p>
                  <p className="mt-2 text-xs leading-relaxed text-zinc-400">{o.detail}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="border-t border-zinc-900 py-16">
        <div className="mx-auto max-w-[1500px] px-5 md:px-10">
          <p className="eyebrow">County by county</p>
          <h2 className="display-2 mt-4">The eighteen catchments</h2>
          <div className="no-scrollbar mt-8 overflow-x-auto border border-white/10">
            <table className="w-full min-w-[820px] text-left">
              <thead>
                <tr className="border-b border-white/15 bg-[#0b0b0b]">
                  {["County", "Rank", "Population", "Weekend teams", "Academies", "Clubs", "Centre cost", "A year", "Pack"].map((h) => (
                    <th key={h} className="px-4 py-3 text-[0.62rem] font-bold uppercase tracking-[0.16em] text-zinc-300">
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody data-testid="national-rows">
                {d.rows.map((r, i) => (
                  <tr key={r.county} className="border-b border-white/5" data-testid={`national-row-${i}`}>
                    <td className="px-4 py-3 text-sm text-white">
                      <Link to={`/counties/${r.county.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} className="hover:text-ochre">
                        {r.county}
                      </Link>
                    </td>
                    <td className="mono px-4 py-3 text-sm text-maasai">{r.rank}</td>
                    <td className="mono px-4 py-3 text-sm text-zinc-300">{r.population.toLocaleString()}</td>
                    <td className="mono px-4 py-3 text-sm text-zinc-300">{r.catchment_teams.toLocaleString()}</td>
                    <td className="mono px-4 py-3 text-sm text-zinc-300">{r.academies}</td>
                    <td className="mono px-4 py-3 text-sm text-zinc-300">{r.professional_clubs}</td>
                    <td className="mono px-4 py-3 text-sm text-ochre">{usd(r.capex_usd)}</td>
                    <td className="mono px-4 py-3 text-sm text-zinc-300">{usd(r.annual_usd)}</td>
                    <td className="px-4 py-3">
                      <a
                        href={`${API}/steps-pack.pdf?track=harambee&county=${encodeURIComponent(r.county)}`}
                        className="text-[0.58rem] font-bold uppercase tracking-[0.14em] text-ochre hover:text-white"
                        data-testid={`national-pack-${i}`}
                      >
                        PDF
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <AfconBand afcon={d.afcon} />

          <div className="mt-10 flex flex-wrap gap-4">
            <Link to="/fifa-ranking" className="btn-ghost" data-testid="national-fifa-link">
              Kenya in the FIFA ranking
            </Link>
            <Link to="/fkf-feed" className="btn-ghost" data-testid="national-feed-link">
              The FKF live feed
            </Link>
          </div>

          <div className="mt-10 border border-white/10 bg-[#0b0b0b] p-7" data-testid="national-sources">
            <p className="overline">How this was costed</p>
            <p className="mt-4 max-w-4xl text-sm leading-relaxed text-zinc-400">{d.note}</p>
            <SourcesLink testId="national-sources-link" />
            <div className="mt-6 flex flex-col gap-4">
              {d.sources.map((s, i) => (
                <div key={s.url}>
                  <a
                    href={s.url}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`national-source-${i}`}
                    className="inline-flex items-center gap-2 text-[0.62rem] uppercase tracking-[0.14em] text-zinc-400 hover:text-ochre"
                  >
                    {s.publisher} <ExternalLink size={11} />
                  </a>
                  <DataStamp source={s} className="!mt-2" testId={`national-source-stamp-${i}`} />
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <StageClose stage="national" />
    </div>
  );
}
